import React, { useEffect, useState } from 'react';
import { Alert, Button, Modal, Skeleton, Tag, Typography, theme } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import getRawDocument from '../../../domain/use_cases/app/getRawDocument';

const { useToken } = theme;

interface RawDocumentModalProps {
  /** Collection Firestore, ex. `Calls`. */
  collection: string;
  /** Id du document à lire. `null` ferme la fenêtre. */
  documentId: string | null;
  /** Document déjà chargé, affiché sans relecture : pour une collection que les règles ferment au client (`Costs`). */
  loadedDocument?: Record<string, unknown> | null;
  onClose: () => void;
}

/**
 * Fenêtre de débogage générique : le document Firestore brut, indenté.
 * Volontairement pas spécifique aux appels — la même fenêtre doit servir sur
 * n'importe quelle table du backoffice, il suffit de lui passer une autre
 * collection.
 */
const RawDocumentModal: React.FC<RawDocumentModalProps> = ({ collection, documentId, loadedDocument = null, onClose }) => {
  const { token } = useToken();
  // Un seul état, estampillé de l'id qu'il décrit. Le chargement s'en DÉDUIT
  // (« le résultat en mémoire ne concerne pas encore la ligne demandée »)
  // au lieu d'être posé dans l'effet, ce qui déclencherait un rendu en
  // cascade — et le `react/set-state-in-effect` du linter avec.
  const [result, setResult] = useState<{
    id: string;
    data: Record<string, unknown> | null;
    error: string | null;
  } | null>(null);

  useEffect(() => {
    if (!documentId || loadedDocument) return;
    let isCurrent = true;
    getRawDocument(collection, documentId)
      .then((doc) => {
        if (!isCurrent) return;
        setResult({
          id: documentId,
          data: doc,
          error: doc ? null : `Document « ${documentId} » introuvable dans ${collection}.`,
        });
      })
      .catch((e: Error) => {
        if (!isCurrent) return;
        setResult({ id: documentId, data: null, error: e.message || 'Lecture impossible.' });
      });
    // Le drapeau évite d'écrire le résultat d'une lecture abandonnée par-dessus
    // celui de la ligne suivante quand on enchaîne deux clics.
    return () => { isCurrent = false; };
  }, [collection, documentId, loadedDocument]);

  const isLoading = documentId !== null && !loadedDocument && result?.id !== documentId;
  const error = isLoading || loadedDocument ? null : result?.error ?? null;
  const data = loadedDocument ?? (isLoading ? null : result?.data ?? null);
  const json = data ? JSON.stringify(data, null, 2) : '';

  return (
    <Modal
      open={documentId !== null}
      onCancel={onClose}
      footer={null}
      width={760}
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Tag>{collection}</Tag>
          <Typography.Text code copyable={{ text: documentId ?? '' }}>{documentId}</Typography.Text>
        </span>
      }
    >
      {isLoading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : error ? (
        <Alert type="error" showIcon message="Lecture impossible" description={error} />
      ) : (
        <>
          <Button
            size="small"
            icon={<CopyOutlined />}
            onClick={() => navigator.clipboard?.writeText(json)}
            style={{ marginBottom: token.marginXS }}
          >
            Copier le JSON
          </Button>
          <pre
            style={{
              margin: 0,
              maxHeight: '60vh',
              overflow: 'auto',
              padding: token.paddingSM,
              borderRadius: token.borderRadius,
              background: token.colorFillQuaternary,
              color: token.colorText,
              fontSize: 12,
              lineHeight: 1.55,
              whiteSpace: 'pre',
            }}
          >
            {json}
          </pre>
        </>
      )}
    </Modal>
  );
};

export default RawDocumentModal;
